import type { QueryClient } from '@tanstack/react-query';

import { isSystemKind } from '#modules/chat/utils/systemMessage';
import { gqlFetcher, graphql } from '#features/api';
import type { ResultOf } from '#features/api/utils/graphql';
import { isDefined } from '#utils/isDefined';
import type { CurrentSessionCandidate } from './currentSession';

/**
 * Запрос под комнату сессии – свой, а не `PostFragment`.
 *
 * Комната висит в корне лейаута на каждой странице приложения, то есть её запрос платят все
 * экраны сразу. `PostFragment` тянет к каждой сессии текст заявки, языки, страну автора, его
 * игровую активность, настройки поста и канал Discord – ничего из этого комната не рисует.
 */
const sessionRoomSessionsDoc = graphql(`
  query SessionRoomSessions($limit: Int) {
    post {
      mySessions(archived: false, offset: 0, limit: $limit) {
        __typename
        ... on PostList {
          posts {
            id
            userId
            userData {
              avatar
              displayName
            }
            gameData {
              id
              name
              icon
              iconColor
              logo
              desktopImage
            }
            activeSession {
              id
              postId
              startedAt
              expiredAt
              status
              teamLimit
              voiceMode
              acceptedMembersCount
              # Вместимость комнаты не просим: потолок голоса – это teamLimit сессии, он уже
              # рядом, а maxParticipants разбор не читал и в SessionRoomEntry его нет.
              voiceRoom {
                connectedCount
                status
              }
              sessionChat {
                id
                unreadCount
                lastMessageText
                lastMessageAt
                # Автора последней реплики шлюз не отдаёт вовсе – ни имени, ни отправителя. Но
                # отдаёт её идентификатор, и по нему имя находится в уже загруженной странице
                # сообщений: одно скалярное поле у объекта, который и так спрашивают, дешевле
                # любого второго запроса.
                lastMessageId
              }
              members {
                memberId
                status
                userData {
                  avatar
                  displayName
                }
              }
            }
          }
        }
      }
    }
  }
`);

export type SessionRoomMember = {
  memberId: string;
  /** ACCEPTED | INVITED | DECLINED – состав комнаты показывает только принятых. */
  status: string | null;
  displayName: string | null;
  avatar: string | null;
};

/** Сессия глазами комнаты: время и состояние – для правила показа, остальное – её содержимое. */
export type SessionRoomEntry = CurrentSessionCandidate & {
  postId: string;
  /** Автор заявки, он же хозяин сессии и модератор голоса. */
  hostId: string | null;
  /** Запасные данные автора, пока его строка отсутствует в составе. */
  hostName?: string | null;
  hostAvatar?: string | null;
  game: {
    id: string | null;
    name: string | null;
    icon: string | null;
    /** Цвет силуэта игры: тот же, что красит кружок в списке чатов. */
    iconColor?: string | null;
    logo: string | null;
    desktopImage?: string | null;
  } | null;
  teamLimit: number | null;
  /** PLATFORM | DISCORD | NONE. */
  voiceMode: string | null;
  acceptedMembersCount: number;
  voiceConnectedCount: number;
  /** OPEN | CLOSED, `null` – комнаты голоса ещё нет. */
  voiceRoomStatus: string | null;
  /** Чат сессии появляется не мгновенно: до него комната пишет «Чат создаётся…». */
  chatId: string | null;
  unreadCount: number;
  lastMessageText: string | null;
  lastMessageAt: string | null;
  /**
   * Идентификатор последней реплики – ключ к её автору (`resolveLastMessageAuthor`).
   *
   * Необязательное поле: комната жила без него и живёт дальше – превью тогда просто без имени.
   * Требовать его от каждого снимка сессии значило бы переписать все, кто такой снимок собирает,
   * ради строки, которая и так не обязана быть.
   */
  lastMessageId?: string | null;
  members: SessionRoomMember[];
};

/** Заявка так, как её вернул этот запрос, – чтобы разбор не расходился с самим документом. */
type RoomPost = NonNullable<
  NonNullable<
    Extract<
      ResultOf<typeof sessionRoomSessionsDoc>['post']['mySessions'],
      { __typename: 'PostList' }
    >['posts']
  >[number]
>;

/**
 * Ответ сервера → список кандидатов. Заявка без активной сессии и сессия без собственного id
 * отбрасываются здесь, чтобы дальше по коду не пришлось проверять их в каждой строке разметки.
 */
export const toSessionRoomEntries = (
  posts: readonly (RoomPost | null)[] | null | undefined
): SessionRoomEntry[] => {
  const raw = (posts ?? []).filter(isDefined);

  return raw
    .map((post) => {
      const session = post?.activeSession;
      const postId = session?.postId ?? post?.id ?? null;

      if (!session?.id || !postId) {
        return null;
      }

      return {
        sessionId: session.id,
        postId,
        hostId: post.userId ?? null,
        hostName: post.userData?.displayName ?? null,
        hostAvatar: post.userData?.avatar ?? null,
        startedAt: session.startedAt ?? null,
        expiredAt: session.expiredAt ?? null,
        status: session.status ?? null,
        game: post.gameData ?? null,
        teamLimit: session.teamLimit ?? null,
        voiceMode: session.voiceMode ?? null,
        acceptedMembersCount: session.acceptedMembersCount ?? 0,
        voiceConnectedCount: session.voiceRoom?.connectedCount ?? 0,
        voiceRoomStatus: session.voiceRoom?.status ?? null,
        chatId: session.sessionChat?.id ?? null,
        unreadCount: session.sessionChat?.unreadCount ?? 0,
        lastMessageText: session.sessionChat?.lastMessageText ?? null,
        lastMessageAt: session.sessionChat?.lastMessageAt ?? null,
        lastMessageId: session.sessionChat?.lastMessageId ?? null,
        members: (session.members ?? []).filter(isDefined).flatMap((member) =>
          member.memberId
            ? [
                {
                  memberId: member.memberId,
                  status: member.status ?? null,
                  displayName: member.userData?.displayName ?? null,
                  avatar: member.userData?.avatar ?? null,
                },
              ]
            : []
        ),
      } satisfies SessionRoomEntry;
    })
    .filter(isDefined);
};

/**
 * Кеш страниц переписки (`['chatMessages', chatId, 50]`) глазами комнаты.
 *
 * Описан здесь структурно и по минимуму – только те поля, из которых берётся имя автора.
 * Тянуть сюда тип самого запроса значило бы, что любая правка в соседней странице сообщений
 * ломает разбор сессий, которому от неё нужны три поля.
 */
export type CachedChatMessages = {
  pages?:
    | readonly ({
        chat?: {
          members?:
            | readonly ({
                userId?: string | null;
                userData?: { displayName?: string | null } | null;
              } | null)[]
            | null;
        } | null;
        messages?:
          | readonly ({
              id?: string | null;
              /**
               * Серверный номер, лежащий РЯДОМ с клиентским, – примета собственной строки.
               *
               * Своё отправленное сообщение живёт в ленте под номером, который выдал браузер
               * (`useChatActions`: `id: clientId, serverId: result.id`), чтобы подтверждение
               * не сносило узел и переписка не дёргалась. Строка сессии при этом знает только
               * серверный номер – по нему свою реплику в ленте и не находили.
               *
               * Ни у одной чужой строки этого поля нет: его ставит только собственная отправка.
               */
              serverId?: string | null;
              senderId?: string | null;
              kind?: string | null;
              /** О ком служебная строка: «вступил», «вышел», «зашёл в голос». */
              systemActorId?: string | null;
              systemActorName?: string | null;
            } | null)[]
          | null;
      } | null)[]
    | null;
} | null;

type LastMessageAuthorInput = {
  /** Последняя реплика переписки по версии строки сессии. Номер всегда серверный. */
  lastMessageId?: string | null;
  /** Состав сессии – запасной словарь имён, когда переписка своего состава ещё не привезла. */
  members?: readonly SessionRoomMember[];
  cache?: CachedChatMessages;
  /**
   * Кто смотрит. Нужен ровно для одного: не подписывать превью именем самого читателя.
   *
   * Необязателен: пока строка своей реплики лежит в ленте под клиентским номером, она узнаётся
   * и без подсказки – по серверному номеру рядом. Но лента живёт своей жизнью и рано или поздно
   * привозит ту же реплику снимком сервера, уже под её настоящим номером и без клиентского.
   * С этого мгновения отличить своё от чужого нечем, кроме как знать, кто читает.
   */
  currentUserId?: string | null;
};

/**
 * Кто написал последнюю реплику – для превью «Ник: го на мид» в пилюле и полосе.
 *
 * Имени автора шлюз не отдаёт ни в одном поле, а без него превью отвечает на половину вопроса:
 * в сессии на пять человек «го на мид» без имени – это реплика неизвестно кого. Второй запрос
 * ради одного имени комната позволить себе не может (она висит на каждой странице приложения),
 * поэтому имя берётся из того, что уже загружено: страницы сообщений этой же переписки.
 *
 * Сверяем по идентификатору, а не берём последнее сообщение из кеша: строка сессии и страница
 * сообщений живут разными ключами и обновляются в разные моменты. Возьми мы «последнее в кеше»,
 * при малейшем расхождении к тексту одной реплики приклеилось бы имя автора другой – а это хуже,
 * чем реплика без имени.
 *
 * Имя ищется сначала в составе переписки, потом в составе сессии: в пузырях ленты имена берутся
 * ровно оттуда, и превью обязано звать человека так же, как окно двумя строками ниже.
 *
 * Служебная строка остаётся без имени: сервер кладёт в неё вид события, а не реплику, и «кто»
 * там додумывать нечем.
 *
 * Своя реплика – тоже без имени, и это не экономия, а правило. Читателю незачем сообщать, как
 * его зовут: имя в превью отвечает на вопрос «кто мне написал», а на своё сообщение такого
 * вопроса нет. Показывать вместо имени «Вы:» тоже не стали – см. запись у `isOwnMessage`.
 *
 * Сверка идёт по ОБОИМ номерам своей строки. В ленте она лежит под клиентским, серверный несёт
 * рядом, а строка сессии знает только серверный: ищи мы по одному `id` – своя реплика не
 * находилась бы вовсе, и превью вело бы себя по-разному до и после того, как лента привезёт ту
 * же реплику снимком сервера (сперва без имени, потом с именем читателя).
 */
export const resolveLastMessageAuthor = ({
  lastMessageId,
  members = [],
  cache,
  currentUserId,
}: LastMessageAuthorInput): string | null => {
  if (!lastMessageId) {
    return null;
  }

  const pages = cache?.pages ?? [];
  const cached = pages.flatMap((page) => page?.messages ?? []);

  /*
    Строку последней реплики ищем по любому из двух её номеров, и по серверному – первым.

    Своя отправленная строка лежит в ленте под клиентским номером, серверный держит рядом; строка
    сессии знает только серверный. Поиск по одному `id` свою реплику не находил вовсе – отсюда и
    пустое имя, и разница в поведении до и после того, как лента привезёт ту же реплику снимком
    сервера. Найденная по `serverId` строка заодно отвечает и на вопрос «своя ли» – других строк
    с двумя номерами в ленте не бывает.
  */
  const own = cached.find((item) => item?.serverId === lastMessageId);
  const message = own ?? cached.find((item) => item?.id === lastMessageId);

  if (!message?.senderId || isSystemKind(message.kind)) {
    return null;
  }

  const { senderId } = message;

  /*
    Своя реплика – без имени. Примет две, и нужны обе.

    Клиентский номер с серверным рядом ставит только собственная отправка – эта примета работает
    даже там, где читатель ещё неизвестен (профиль приезжает своим запросом). Но живёт она
    недолго: лента привозит ту же реплику снимком сервера, под настоящим номером и без
    клиентского, и с этого мгновения своё от чужого отличает только `currentUserId`.

    Почему «ничего», а не «Вы:». Пустое место годится и здесь, и там, где автор просто неизвестен,
    – одна ветка разметки вместо двух и ни одной новой строки каталога перед заморозкой. «Вы:»
    само по себе не хуже (так подписывают превью мессенджеры), но его текст обязан жить макросом
    в пилюле и в полосе: правка обеих поверхностей и перевод на два языка ради подписи к
    сообщению, которое читатель только что написал сам.
  */
  const isOwnMessage = Boolean(own) || (Boolean(currentUserId) && senderId === currentUserId);

  if (isOwnMessage) {
    return null;
  }

  const fromChat = pages
    .flatMap((page) => page?.chat?.members ?? [])
    .find((member) => member?.userId === senderId)?.userData?.displayName;

  const name =
    fromChat ?? members.find((member) => member.memberId === senderId)?.displayName ?? null;

  return name?.trim() || null;
};

/** О ком последняя служебная строка – для превью «Вы вступили» вместо «Участник вступил». */
export type LastSystemActor = {
  isSelf: boolean;
  name: string | null;
  /**
   * Кто это. Нужен, чтобы проверить его по живому составу голоса: строку «вышел из голоса»
   * сервер не пишет намеренно, и без такой сверки превью часами держит «Юра зашёл в голос»
   * рядом с «Никого в голосе».
   */
  userId: string | null;
};

/** Служебные строки, у которых есть подлежащее. Остальные («создана», «закончилась») ни о ком. */
export const PERSON_SYSTEM_KINDS = new Set([
  'SESSION_MEMBER_JOINED',
  'SESSION_MEMBER_LEFT',
  'SESSION_VOICE_JOINED',
]);

/**
 * О ком последняя служебная строка переписки.
 *
 * Строка сессии отдаёт только вид события, и превью говорило «Участник вступил» даже тому, кто
 * вступил сам: вступивший с телефона первым делом читал о себе в третьем лице (прогон 14.09.2026).
 * Сама строка ленты знает, о ком она, – ищем её по номеру среди уже загруженных страниц.
 *
 * `null` – строки нет под рукой или она ни о ком: превью остаётся безличным, как было.
 */
export const resolveLastSystemActor = ({
  lastMessageId,
  lastMessageText,
  cache,
  currentUserId,
}: {
  lastMessageId?: string | null;
  lastMessageText?: string | null;
  cache?: CachedChatMessages;
  currentUserId?: string | null;
}): LastSystemActor | null => {
  if (!lastMessageId || !lastMessageText || !PERSON_SYSTEM_KINDS.has(lastMessageText)) {
    return null;
  }

  const message = (cache?.pages ?? [])
    .flatMap((page) => page?.messages ?? [])
    .find((item) => item?.id === lastMessageId);

  if (!message?.systemActorId) {
    return null;
  }

  return {
    isSelf: Boolean(currentUserId) && message.systemActorId === currentUserId,
    name: message.systemActorName?.trim() || null,
    userId: message.systemActorId ?? null,
  };
};

/**
 * Сессий у человека единицы; десяти хватает, чтобы среди них нашлась и идущая, и ближайшая. Дальше
 * по списку лежат сессии на завтра и позже – комната их всё равно не покажет.
 */
const ROOM_SESSIONS_LIMIT = 10;

export const fetchSessionRoomSessions = (): Promise<SessionRoomEntry[]> =>
  gqlFetcher(sessionRoomSessionsDoc, { limit: ROOM_SESSIONS_LIMIT }).then((data) =>
    data.post.mySessions?.__typename === 'PostList'
      ? toSessionRoomEntries(data.post.mySessions.posts)
      : []
  );

/**
 * Семья ключей «мои сессии». Кеш комнаты живёт внутри неё, и это несущее решение.
 *
 * По этому префиксу список перечитывают все, кто меняет состав или состояние сессии: ветки
 * SESSION_* и VOICE_* обработчика сокета, окно сессии (`SessionDetailsDialog`), его обёртка по id
 * (`SessionDetailsDialogById`), кебаб комнаты, окно создания сбора, полоса сессии в переписке.
 * Дай комнате собственный ключ – и каждый из них пришлось бы учить второму имени; забыл бы один –
 * комната узнавала бы об отмене и о выходе только по F5.
 */
export const MY_SESSIONS_QUERY_FAMILY = ['my-sessions'] as const;

export const sessionRoomSessionsQueryKey = ['my-sessions', 'room'] as const;

/**
 * Перечитать список комнаты после собственного действия над сессией – отмены, завершения, выхода.
 *
 * Названная точка входа вместо голого массива у каждого вызывающего: инвалидация здесь не
 * «обновить на всякий случай», а обязательный хвост трёх мутаций. Собственное действие – это как
 * раз тот случай, когда помощи ждать неоткуда: своё же событие сокета до актора не доходит
 * (бэкенд гасит его как SKIPPED_SELF_ACTOR), а `refetchOnWindowFocus` в приложении выключен
 * глобально – значит без этого вызова комната стоит с отменённой сессией, пока кто-нибудь не
 * заденет кеш чем-то посторонним.
 *
 * Инвалидируем семью целиком, а не один ключ комнаты: те же три действия меняют и списки сессий
 * на странице заявки, и полосу сессии в переписке.
 */
export const invalidateSessionRoomSessions = (queryClient: QueryClient): Promise<void> =>
  queryClient.invalidateQueries({ queryKey: MY_SESSIONS_QUERY_FAMILY });

export const sessionRoomSessionsQueryOptions = () => ({
  queryKey: sessionRoomSessionsQueryKey,
  queryFn: fetchSessionRoomSessions,
  // Свежесть держит сокет; короткое окно снимает лишь повтор запроса на переходах между
  // страницами, где комната перерисовывается вместе с лейаутом.
  staleTime: 15_000,
  /*
    Плюс опрос – страховка от недоехавшего кадра (12.09.2026).

    Комната висит поверх ЛЮБОЙ страницы и на многих из них она единственное, что человек видит
    о сессии: её вторая строка и есть последняя реплика. Сокет обычно быстрее, но кадр
    `SESSION_CHAT_MESSAGE` иногда не доезжает, и тогда окно держит «Сессия создана» рядом с
    перепиской, где уже три сообщения. На стенде 12.09 это поймали дважды подряд.

    Двадцать секунд – тот же интервал, что у снимка голосовой комнаты (`voice.ts`), и по той же
    причине: предмет, который врёт молча, дороже одного запроса в двадцать секунд.
  */
  refetchInterval: 20_000,
});
